import { Navigator as RouterNavigator, parsePath } from "react-router-dom";

const BEFORE_UNLOAD_EVENT = "beforeunload";
const NAVIGATE_EVENT = "navigate";
const TRAVERSE_NAVIGATION_TYPE = "traverse";

// Takes back a question that is still unanswered.
export type TWithdrawQuestion = () => void;

// Asks the broker whether to leave the page; runs `leave` only once they
// confirm. Returns how to take the question back.
export type TAskToLeave = (leave: () => void) => TWithdrawQuestion;

type TListenerTarget = Pick<
  EventTarget,
  "addEventListener" | "removeEventListener"
>;

// The parts of the browser's Navigation API the guard uses; this TypeScript
// version's DOM lib does not declare them yet.
export interface INavigateEvent extends Event {
  navigationType: string;
  destination: { key: string; url: string };
}

export interface ITraversalNavigation extends TListenerTarget {
  traverseTo: (key: string) => unknown;
}

export interface ILeaveGuardParams {
  // Where beforeunload arrives: the window.
  target: TListenerTarget;
  location: Pick<Location, "pathname">;
  // The router's navigator, which every in-app link and redirect goes through.
  navigator: RouterNavigator;
  // window.navigation; missing in browsers without the Navigation API.
  navigation?: ITraversalNavigation;
  // Checked at the moment of leaving, not at install: a save that just
  // finished redirects before the page re-renders.
  shouldAsk: () => boolean;
  askToLeave: TAskToLeave;
}

// An installed guard as the watchers of a way of leaving see it.
interface IInstalledGuard {
  location: Pick<Location, "pathname">;
  // False once the guard is gone, also in an attempt that began before.
  shouldAsk: () => boolean;
  // Replaces the guard's own open question, if any.
  askToLeave: TAskToLeave;
  withdrawQuestion: TWithdrawQuestion;
}

// The guards installed at once on one router navigator or one navigation, in
// install order, e.g. the settlement wizard and the cost popup opened in it.
// They share one watcher, so a single way out asks each question once instead
// of every guard holding the same move on its own.
interface IGuardGroup {
  guards: IInstalledGuard[];
  stopWatching: () => void;
}

const routerGuardGroups = new WeakMap<RouterNavigator, IGuardGroup>();
const traversalGuardGroups = new WeakMap<ITraversalNavigation, IGuardGroup>();

const startGuardGroup = (
  startWatching: (guards: IInstalledGuard[]) => () => void,
): IGuardGroup => {
  const guards: IInstalledGuard[] = [];
  return { guards, stopWatching: startWatching(guards) };
};

// Adds the guard to the group on `watched`: the group starts watching with
// its first guard and stops with its last, whatever order they go in.
// Returns how the guard leaves the group.
const joinGuardGroup = <TWatched extends object>(params: {
  groups: WeakMap<TWatched, IGuardGroup>;
  watched: TWatched;
  guard: IInstalledGuard;
  startWatching: (guards: IInstalledGuard[]) => () => void;
}): (() => void) => {
  const { groups, watched, guard, startWatching } = params;
  const group = groups.get(watched) ?? startGuardGroup(startWatching);
  groups.set(watched, group);
  group.guards.push(guard);

  return () => {
    const index = group.guards.indexOf(guard);
    if (index < 0) return;
    group.guards.splice(index, 1);
    if (group.guards.length) return;
    group.stopWatching();
    groups.delete(watched);
  };
};

// The guard installed last asks first: the cost popup before the wizard it
// was opened in.
const lastInstalledFirst = (guards: IInstalledGuard[]) => [...guards].reverse();

// Asks the guards' questions one at a time, in the order given, and leaves
// once every guard that still asks by its turn has its "leave anyway".
// Cancelling a question ends the attempt there.
const askInTurn = (params: {
  guards: IInstalledGuard[];
  isAsking: (guard: IInstalledGuard) => boolean;
  leave: () => void;
}) => {
  const { guards, isAsking, leave } = params;
  const index = guards.findIndex(isAsking);
  if (index < 0) {
    leave();
    return;
  }
  guards[index].askToLeave(() =>
    askInTurn({ guards: guards.slice(index + 1), isAsking, leave }),
  );
};

// Reload and closing the tab: the browser's own "leave site?" prompt.
const guardUnload = (
  params: Pick<ILeaveGuardParams, "target" | "shouldAsk">,
): (() => void) => {
  const { target, shouldAsk } = params;
  const handleBeforeUnload = (event: Event) => {
    if (!shouldAsk()) return;
    event.preventDefault();
    // INFO: some browsers only ask when returnValue is set.
    (event as BeforeUnloadEvent).returnValue = "";
  };
  target.addEventListener(BEFORE_UNLOAD_EVENT, handleBeforeUnload);
  return () =>
    target.removeEventListener(BEFORE_UNLOAD_EVENT, handleBeforeUnload);
};

// Browser back and forward. The router only hears of them through popstate,
// once the browser has already moved; the Navigation API's navigate event
// comes before that and can hold the move. Once every question is answered
// with "leave anyway" the watcher makes the same move itself and lets it
// through.
// INFO: against back-button traps the browser only lets a page hold such a
// move again after a click on it, so pressing back twice without answering
// the question still leaves; the question then goes with the page. Browsers
// without the Navigation API (before Safari 26.2 and Firefox 147) are not
// guarded here.
const watchHistoryTraversal = (params: {
  navigation: ITraversalNavigation;
  guards: IInstalledGuard[];
}): (() => void) => {
  const { navigation, guards } = params;

  // The entry the broker confirmed leaving to.
  let leavingToKey: string | null = null;

  const handleNavigate = (event: Event) => {
    const { navigationType, destination, cancelable } = event as INavigateEvent;
    if (navigationType !== TRAVERSE_NAVIGATION_TYPE) return;
    const isConfirmed = destination.key === leavingToKey;
    leavingToKey = null;
    if (isConfirmed) return;
    // Another entry of a guard's own page, e.g. only another query, keeps it.
    const { pathname } = new URL(destination.url);
    const leaving = guards.filter(
      guard => guard.location.pathname !== pathname,
    );
    const asking = cancelable ? leaving.filter(guard => guard.shouldAsk()) : [];
    if (!asking.length) {
      // The page is left anyway, so an open question no longer applies: its
      // "leave anyway" would only move to the entry already shown.
      leaving.forEach(guard => guard.withdrawQuestion());
      return;
    }
    event.preventDefault();
    askInTurn({
      guards: lastInstalledFirst(asking),
      isAsking: guard => guard.shouldAsk(),
      leave: () => {
        leavingToKey = destination.key;
        navigation.traverseTo(destination.key);
      },
    });
  };

  navigation.addEventListener(NAVIGATE_EVENT, handleNavigate);
  return () => navigation.removeEventListener(NAVIGATE_EVENT, handleNavigate);
};

// In-app navigation: a sidebar item, a breadcrumb, any link or redirect.
// Staying on the same path, e.g. only changing the query, does not leave.
// INFO: navigate(-1) and the like move through the browser history, which
// watchHistoryTraversal covers.
const watchRouterNavigation = (params: {
  navigator: RouterNavigator;
  guards: IInstalledGuard[];
}): (() => void) => {
  const { navigator, guards } = params;
  const { push, replace } = navigator;

  const guardMove =
    (move: RouterNavigator["push"]): RouterNavigator["push"] =>
    (to, state, opts) => {
      const { pathname } = typeof to === "string" ? parsePath(to) : to;
      askInTurn({
        guards: lastInstalledFirst(guards),
        isAsking: guard =>
          !!pathname &&
          pathname !== guard.location.pathname &&
          guard.shouldAsk(),
        leave: () => move(to, state, opts),
      });
    };

  navigator.push = guardMove(push);
  navigator.replace = guardMove(replace);

  return () => {
    navigator.push = push;
    navigator.replace = replace;
  };
};

// Asks before any way of leaving the page: reload or closing the tab, browser
// back or forward, and every in-app navigation. Guards installed at the same
// time, like the cost popup's inside the settlement wizard, ask in turn, the
// last installed first, and the page is left once all of them are answered
// with "leave anyway". Returns the undo, which also takes back a question
// still open: it must not outlive the page it guards.
export const installLeaveGuard = (params: ILeaveGuardParams): (() => void) => {
  const { target, location, navigator, navigation } = params;
  let isInstalled = true;
  let withdrawOpenQuestion: TWithdrawQuestion | null = null;
  const withdrawQuestion = () => {
    withdrawOpenQuestion?.();
    withdrawOpenQuestion = null;
  };
  // A new question replaces the open one.
  const askToLeave: TAskToLeave = leave => {
    withdrawQuestion();
    const withdraw = params.askToLeave(() => {
      withdrawOpenQuestion = null;
      leave();
    });
    withdrawOpenQuestion = withdraw;
    return withdraw;
  };
  const guard: IInstalledGuard = {
    location,
    shouldAsk: () => isInstalled && params.shouldAsk(),
    askToLeave,
    withdrawQuestion,
  };

  const removers = [
    guardUnload({ target, shouldAsk: guard.shouldAsk }),
    joinGuardGroup({
      groups: routerGuardGroups,
      watched: navigator,
      guard,
      startWatching: guards => watchRouterNavigation({ navigator, guards }),
    }),
  ];
  if (navigation) {
    removers.push(
      joinGuardGroup({
        groups: traversalGuardGroups,
        watched: navigation,
        guard,
        startWatching: guards => watchHistoryTraversal({ navigation, guards }),
      }),
    );
  }
  return () => {
    isInstalled = false;
    removers.forEach(remove => remove());
    withdrawQuestion();
  };
};
